import { Formik } from 'formik';
import { useCurrentAccountCountryQueryOptions } from 'hooks/common/useCurrentAccountCountries';
import { useIsEnterpriseCompanyAccount } from 'hooks/company/useCompanyAccountType';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  DefaultModalProps,
  useOpenModal,
  withLocationStateModal,
} from 'utils/modals';
import * as yup from 'yup';

import { Alert } from '@mui/lab';
import { Box, FormGroup, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';

import {
  GetCountiresListDocument,
  TalentCategoriesDocument,
  useGetCurrentUserCompanyTagsQuery,
} from '@libs/graphql-types';
import { modelPath } from '@libs/helpers/form';
import Button from '@libs/ui/components/button';
import { ConnectedCheckbox } from '@libs/ui/components/form/checkbox';
import { ConnectedMultipleSelect } from '@libs/ui/components/form/multiple-select';
import {
  ConnectedGraphSelect,
  OptionType,
} from '@libs/ui/components/form/select';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import { DefaultModal } from '@libs/ui/components/modals';
import Typography from '@libs/ui/components/typography';

import { ProfileModals } from '../types';
import { MESSAGE_TYPES } from './consts';
import { useSubmitAction } from './hooks';
import { TalentsCount } from './talents-count';
import { FormModel } from './types';

const useStyles = makeStyles(() => ({
  paper: {
    width: 620,

    '& textarea': {
      resize: 'vertical',
    },
  },
  content: {
    textAlign: 'left',
  },
}));

const validation = yup.object().shape({
  category: yup.string().nullable(),
  messageType: yup.string().nullable().required(),
  message: yup.string().nullable().trim().required(),
});

interface MessageToWorkforceProps {
  scopedCompanyId: string | undefined;
}

const MessageToWorkforce = ({
  isOpen,
  close,
  modalData,
}: DefaultModalProps<MessageToWorkforceProps>) => {
  const { t } = useTranslation('workforce');
  const scopedCompanyId = modalData?.scopedCompanyId;
  const classes = useStyles();
  const isEnterprise = useIsEnterpriseCompanyAccount();
  const { onSubmit, loading } = useSubmitAction({ onSuccess: close });
  const countriesQueryOptions = useCurrentAccountCountryQueryOptions();
  const { data: tagsData } = useGetCurrentUserCompanyTagsQuery();
  const tagOptions = (tagsData?.currentUserCompanyTags || []).map((i) => ({
    text: i?.name || '',
    value: i?.id || '',
  }));

  return (
    <Formik<FormModel>
      initialValues={{
        category: '',
        messageType: MESSAGE_TYPES.NO_REPLY as any,
        message: '',
        tags: [],
        sendEmail: true,
        isImportant: false,
        country: '',
        subject: '',
        zipCode: [],
        includeActive: true,
        includeStatic: false,
      }}
      onSubmit={onSubmit}
      validationSchema={validation}
    >
      {({ handleSubmit, values, setFieldValue }) => {
        return (
          <DefaultModal
            handleClose={close}
            open={isOpen}
            title={t('messageWorkforce.title')}
            className={classes.paper}
            actions={
              <Grid spacing={4} justifyContent="space-between" container>
                <Grid xs={6} item>
                  <Button
                    fullWidth
                    color="primary"
                    variant="contained"
                    onClick={() => handleSubmit()}
                    disabled={loading}
                  >
                    {t('messageWorkforce.sendMessage')}
                  </Button>
                </Grid>
                <Grid xs={6} item>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={close}
                    disabled={loading}
                  >
                    {t('messageWorkforce.cancel')}
                  </Button>
                </Grid>
              </Grid>
            }
          >
            <Box pb={6}>
              {!isEnterprise && (
                <Typography>{t('messageWorkforce.subtitle')}</Typography>
              )}
            </Box>

            <Grid
              className={classes.content}
              container
              spacing={4}
              direction="column"
            >
              <Grid item>
                <ConnectedGraphSelect
                  query={GetCountiresListDocument}
                  dataPath="getCountriesList"
                  dataMap={{ text: 'name', value: 'name' }}
                  hideNoneValue
                  formControlProps={{ size: 'small' }}
                  fullWidth
                  queryOptions={{ variables: countriesQueryOptions }}
                  label={t('messageWorkforce.country')}
                  name={modelPath<FormModel>((m) => m.country)}
                  variant="filled"
                />
                {/* <GraphConnectedMultipleSelect */}
                {/*   query={GetCountiresListDocument} */}
                {/*   dataMap={{ text: 'name', value: 'name' }} */}
                {/*   dataPath="getCountriesList" */}
                {/*   hideNoneValue */}
                {/*   queryOptions={{ variables: { is_europe: true } }} */}
                {/*   formControlProps={{ size: 'small' }} */}
                {/*   fullWidth */}
                {/*   autoCompleteProps={{ */}
                {/*     isOptionEqualToValue: ( */}
                {/*       option: OptionType, */}
                {/*       value: OptionType, */}
                {/*     ) => { */}
                {/*       return value.value === option.value; */}
                {/*     }, */}
                {/*     filterSelectedOptions: true, */}
                {/*   }} */}
                {/*   inputProps={{ */}
                {/*     variant: 'filled', */}
                {/*     label: 'Select countries', */}
                {/*     placeholder: '', */}
                {/*     margin: 'dense', */}
                {/*     fullWidth: true, */}
                {/*   }} */}
                {/*   helperText="Multiple selections allowed" */}
                {/*   name={modelPath<FormModel>((m) => m.countries)} */}
                {/*   variant="filled" */}
                {/* /> */}
              </Grid>
              <Grid item>
                <ConnectedMultipleSelect
                  fullWidth
                  name={modelPath<FormModel>((m) => m.zipCode)}
                  options={[]}
                  variant="filled"
                  label={t('messageWorkforce.zipCode')}
                  getOptionLabel={(opt: any) => opt}
                  autoCompleteProps={{
                    filterSelectedOptions: true,
                    freeSolo: true,
                    isOptionEqualToValue: (
                      opt: OptionType,
                      value: OptionType,
                    ) => opt.value === value.value,
                  }}
                  chipProps={{
                    size: 'small',
                  }}
                  inputProps={{
                    variant: 'filled',
                    label: t('messageWorkforce.filterByZipCode'),
                    margin: 'dense',
                  }}
                />
              </Grid>
              {!!tagOptions.length && (
                <Grid item>
                  <ConnectedMultipleSelect
                    fullWidth
                    name={modelPath<FormModel>((m) => m.tags)}
                    variant="filled"
                    options={tagOptions}
                    label={t('messageWorkforce.pickFromList')}
                    autoCompleteProps={{
                      filterSelectedOptions: true,
                      isOptionEqualToValue: (
                        opt: OptionType,
                        value: OptionType,
                      ) => opt.value === value.value,
                    }}
                    chipProps={{
                      size: 'small',
                    }}
                    inputProps={{
                      variant: 'filled',
                      label: t('messageWorkforce.filterByTags'),
                      margin: 'dense',
                    }}
                  />
                </Grid>
              )}
              <Grid item>
                <ConnectedGraphSelect
                  query={TalentCategoriesDocument}
                  dataPath="talentCategories"
                  formControlProps={{ size: 'small' }}
                  fullWidth
                  name={modelPath<FormModel>((m) => m.category)}
                  variant="filled"
                  label={t('messageWorkforce.filterByCategory')}
                />
              </Grid>
              <Grid item>
                <Typography variant="subtitle2" gutterBottom>
                  {t('messageWorkforce.statusFilter')}
                </Typography>
                <FormGroup row>
                  <ConnectedCheckbox
                    name={modelPath<FormModel>((m) => m.includeActive)}
                    label={t('messageWorkforce.active')}
                  />
                  <ConnectedCheckbox
                    name={modelPath<FormModel>((m) => m.includeStatic)}
                    label={t('messageWorkforce.static')}
                  />
                </FormGroup>
              </Grid>
              <Grid item>
                <ConnectedTextField
                  fullWidth
                  variant="filled"
                  label={t('messageWorkforce.subject')}
                  name={modelPath<FormModel>((m) => m.subject)}
                />
              </Grid>
              <Grid item>
                <ConnectedCheckbox
                  label={t('messageWorkforce.isImportant')}
                  name={modelPath<FormModel>((m) => m.isImportant)}
                />
              </Grid>
              <Grid item>
                <ConnectedCheckbox
                  label={t('messageWorkforce.sendEmail')}
                  name={modelPath<FormModel>((m) => m.sendEmail)}
                />
              </Grid>

              <Grid item>
                <ConnectedTextField
                  fullWidth
                  size="small"
                  variant="filled"
                  onPaste={(e) => {
                    e.persist();
                    e.preventDefault();
                    const currentMessage = values.message;
                    const text = e.clipboardData.getData('text');
                    // Remove extra newlines from the text
                    const cleanedText = text.replace(/\n{3,}/g, '\n\n');

                    // Get the current cursor position
                    let cursorPos: number =
                      (e.nativeEvent?.target as any)?.selectionStart || 0;

                    // Split the textarea content at the cursor position
                    let textBeforeCursor = currentMessage.substring(
                      0,
                      cursorPos,
                    );
                    let textAfterCursor = currentMessage.substring(
                      cursorPos,
                      currentMessage.length,
                    );

                    const newText =
                      textBeforeCursor + cleanedText + textAfterCursor;
                    setFieldValue(
                      modelPath<FormModel>((m) => m.message),
                      newText,
                    );
                  }}
                  multiline
                  label={t('messageWorkforce.enterMessage')}
                  name={modelPath<FormModel>((m) => m.message)}
                  rows={6}
                />
              </Grid>
            </Grid>
            <br />
            <TalentsCount values={values} scopedCompanyId={scopedCompanyId} />

            {loading && (
              <Box mt={4}>
                <Alert color="info">
                  {t('messageWorkforce.operationInProgress')}
                </Alert>
              </Box>
            )}
          </DefaultModal>
        );
      }}
    </Formik>
  );
};

export const MessageToWorkforceModal =
  withLocationStateModal<MessageToWorkforceProps>({
    id: ProfileModals.MessageToWorkforce,
  })(MessageToWorkforce);

export const useOpenMessageToWorkforceModal = () => {
  const openModal = useOpenModal(ProfileModals.MessageToWorkforce);

  return ({ scopedCompanyId }: MessageToWorkforceProps) =>
    openModal({ scopedCompanyId });
};
